import { useState, useEffect } from "react";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Building, 
  Plus, 
  Search, 
  Download,
  Edit
} from "lucide-react";

export default function BuildingCodes() {
  const { user } = useAuth();
  const [buildingCodes, setBuildingCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    address: "",
    building_code: "",
    notes: ""
  });

  // Check if user can export
  const canExport = user?.is_admin || user?.role === 'admin' || user?.is_dispatcher || user?.role === 'dispatcher';

  // Fetch building codes
  const fetchBuildingCodes = async () => {
    try {
      const response = await axios.get(`${API}/building-codes`, {
        withCredentials: true
      });
      setBuildingCodes(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch building codes:", error);
      toast.error("Failed to load building codes");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildingCodes();
  }, []);

  // Handle mobile back button for building code detail
  useBackButton(showMobileDetail, () => setShowMobileDetail(false));

  // Add new building code
  const handleAdd = async () => {
    if (!formData.address || !formData.building_code) {
      toast.error("Address and building code are required");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/building-codes`,
        formData,
        { withCredentials: true }
      );
      setBuildingCodes([...buildingCodes, response.data]);
      setFormData({ address: "", building_code: "", notes: "" });
      setShowAddDialog(false);
      toast.success("Building code added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add building code");
    }
  };

  // Edit building code
  const handleEdit = async () => {
    if (!formData.address || !formData.building_code) {
      toast.error("Address and building code are required");
      return;
    }

    try {
      const response = await axios.put(
        `${API}/building-codes/${selectedCode.code_id}`,
        formData,
        { withCredentials: true }
      );
      setBuildingCodes(buildingCodes.map(bc => 
        bc.code_id === selectedCode.code_id ? response.data : bc
      ));
      setSelectedCode(response.data);
      setShowEditDialog(false);
      toast.success("Building code updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update building code");
    }
  };

  // Export to CSV
  const handleExport = () => {
    if (!canExport) {
      toast.error("You don't have permission to export");
      return;
    }

    const headers = ["Address", "Building Code", "Notes", "Created By", "Created At", "Last Updated By", "Last Updated At", "Edit History"];
    const rows = sortedCodes.map(code => {
      // Format edit history as readable string
      let editHistoryText = "";
      if (code.edit_history && code.edit_history.length > 0) {
        editHistoryText = code.edit_history.map(edit => {
          const changes = edit.changes.map(change => 
            `${change.field}: "${change.old_value}" → "${change.new_value}"`
          ).join("; ");
          return `[${new Date(edit.edited_at).toLocaleString("en-US", { timeZone: "America/New_York" })}] ${edit.edited_by_name}: ${changes}`;
        }).join(" | ");
      }

      return [
        code.address,
        code.building_code,
        code.notes || "",
        code.created_by_name || "",
        new Date(code.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }),
        code.updated_by_name || code.updated_by || "",
        code.updated_at ? new Date(code.updated_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "",
        editHistoryText
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `building-codes-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("Exported building codes with edit history");
  };

  // Filter and sort
  const filteredCodes = buildingCodes.filter(code => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      code.address?.toLowerCase().includes(search) ||
      code.building_code?.toLowerCase().includes(search) ||
      code.notes?.toLowerCase().includes(search)
    );
  });

  const sortedCodes = [...filteredCodes].sort((a, b) => 
    a.address.localeCompare(b.address)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-4 gap-4">
      {/* Header */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building className="w-6 h-6 text-blue-500" />
              <CardTitle className="text-xl uppercase tracking-wider">
                Building Codes
              </CardTitle>
              <Badge variant="outline" className="ml-2">
                {sortedCodes.length} Buildings
              </Badge>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Building
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle>Add Building Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Address *</Label>
                    <Input
                      placeholder="123 Main St"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>Building Code *</Label>
                    <Input
                      placeholder="B-123"
                      value={formData.building_code}
                      onChange={(e) => setFormData({ ...formData, building_code: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input
                      placeholder="Additional information..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setShowAddDialog(false);
                      setFormData({ address: "", building_code: "", notes: "" });
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleAdd}>Add</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* List */}
        <div className="w-full lg:w-1/3 min-w-0 bg-slate-900 rounded-lg border border-slate-800 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-3 border-b border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by address, code, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          {/* List */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {sortedCodes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No building codes found</p>
                </div>
              ) : (
                sortedCodes.map((code) => (
                  <Card
                    key={code.code_id}
                    className={`cursor-pointer transition-colors ${
                      selectedCode?.code_id === code.code_id
                        ? "bg-blue-900/30 border-blue-500"
                        : "bg-slate-800 border-slate-700 hover:bg-slate-700"
                    }`}
                    onClick={() => {
                      setSelectedCode(code);
                      // On mobile, show the detail dialog
                      if (window.innerWidth < 1024) {
                        setShowMobileDetail(true);
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{code.address}</p>
                        <Badge className="text-xs">{code.building_code}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Detail */}
        <div className="flex-1 bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
          {selectedCode ? (
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{selectedCode.address}</h2>
                  <Badge className="text-lg px-3 py-1">{selectedCode.building_code}</Badge>
                </div>
                <div className="flex gap-2">
                  <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData({
                          address: selectedCode.address,
                          building_code: selectedCode.building_code,
                          notes: selectedCode.notes || ""
                        })}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-700">
                      <DialogHeader>
                        <DialogTitle>Edit Building Code</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Address *</Label>
                          <Input
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label>Building Code *</Label>
                          <Input
                            value={formData.building_code}
                            onChange={(e) => setFormData({ ...formData, building_code: e.target.value })}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Input
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleEdit}>Save</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Details */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <div>
                    <Label className="text-xs text-slate-500">Address</Label>
                    <p className="text-sm">{selectedCode.address}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Building Code</Label>
                    <p className="text-sm">{selectedCode.building_code}</p>
                  </div>
                  {selectedCode.notes && (
                    <div>
                      <Label className="text-xs text-slate-500">Notes</Label>
                      <p className="text-sm">{selectedCode.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit History */}
              {selectedCode.edit_history && selectedCode.edit_history.length > 0 && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Edit History ({selectedCode.edit_history.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ScrollArea className="h-48">
                      <div className="space-y-3">
                        {selectedCode.edit_history.map((edit, idx) => (
                          <div key={idx} className="pb-3 border-b border-slate-700 last:border-0">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-slate-300">
                                {edit.edited_by_name || edit.edited_by}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(edit.edited_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                              </span>
                            </div>
                            <div className="space-y-1">
                              {edit.changes.map((change, changeIdx) => (
                                <div key={changeIdx} className="text-xs bg-slate-900 p-2 rounded">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs">
                                      {change.field === 'building_code' ? 'Code' : 
                                       change.field.charAt(0).toUpperCase() + change.field.slice(1)}
                                    </Badge>
                                  </div>
                                  <div className="space-y-0.5">
                                    <div className="flex items-start gap-2">
                                      <span className="text-red-400 flex-shrink-0">Old:</span>
                                      <span className="text-slate-400 line-through break-all">
                                        {change.old_value || '(empty)'}
                                      </span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <span className="text-green-400 flex-shrink-0">New:</span>
                                      <span className="text-slate-200 break-all">
                                        {change.new_value || '(empty)'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500">
              <div className="text-center">
                <Building className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select a building to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Detail Dialog */}
      <Dialog open={showMobileDetail} onOpenChange={setShowMobileDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 max-h-[85vh] overflow-y-auto">
          {selectedCode && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedCode.address}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Badge className="text-lg px-3 py-1">{selectedCode.building_code}</Badge>
                
                {selectedCode.notes && (
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Notes</p>
                    <p className="text-slate-200">{selectedCode.notes}</p>
                  </div>
                )}
                
                {/* Edit History */}
                {selectedCode.edit_history && selectedCode.edit_history.length > 0 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Edit History ({selectedCode.edit_history.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ScrollArea className="h-48">
                        <div className="space-y-3">
                          {selectedCode.edit_history.map((edit, idx) => (
                            <div key={idx} className="pb-3 border-b border-slate-700 last:border-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-slate-300">
                                  {edit.edited_by_name || edit.edited_by}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {new Date(edit.edited_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {edit.changes.map((change, changeIdx) => (
                                  <div key={changeIdx} className="text-xs bg-slate-900 p-2 rounded">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge variant="outline" className="text-xs">
                                        {change.field === 'building_code' ? 'Code' :
                                         change.field.charAt(0).toUpperCase() + change.field.slice(1)}
                                      </Badge>
                                    </div>
                                    <div className="space-y-0.5">
                                      <div className="flex items-start gap-2">
                                        <span className="text-red-400 flex-shrink-0">Old:</span>
                                        <span className="text-slate-400 line-through break-all">
                                          {change.old_value || '(empty)'}
                                        </span>
                                      </div>
                                      <div className="flex items-start gap-2">
                                        <span className="text-green-400 flex-shrink-0">New:</span>
                                        <span className="text-slate-200 break-all">
                                          {change.new_value || '(empty)'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setFormData({
                        address: selectedCode.address,
                        building_code: selectedCode.building_code,
                        notes: selectedCode.notes || ""
                      });
                      setShowMobileDetail(false);
                      setShowEditDialog(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowMobileDetail(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
